import { Button, Divider } from "antd";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import type { DataTableFilter } from "../../types/dataTableFilter";

type FilterPanelContentProps = {
  filters: DataTableFilter[];
  onChangeFilters: (filterKey: string, values: any) => void;
  currentFilter: Record<string, any>;
};

type GroupedFilters = {
  [header: string]: DataTableFilter[];
};

export const FilterPanelContent = ({ filters, onChangeFilters, currentFilter }: FilterPanelContentProps) => {
  const [pendingFilters, setPendingFilters] = useState<Record<string, any>>({});

  const handleFilterChange = (filterKey: string, value: any) => {
    setPendingFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  const handleApplyFilters = () => {
    Object.keys(pendingFilters).forEach((key) => {
      onChangeFilters(key, pendingFilters[key]);
    });
    setPendingFilters({});
  };

  const handleClearFilters = () => {
    filters.forEach((filter) => {
      if (currentFilter[filter.filterKey]) onChangeFilters(filter.filterKey, undefined);
    });
    setPendingFilters({});
  };

  const groupedFilters = filters.reduce((acc: GroupedFilters, filter) => {
    const header = filter.secondaryFilterHeader || "default";
    if (!acc[header]) {
      acc[header] = [];
    }
    acc[header].push(filter);
    return acc;
  }, {});

  const renderFilterElement = (filter: DataTableFilter) => (
    <div key={filter.filterKey} className="filter-grid-item">
      {typeof filter.filterElement === "function"
        ? filter.filterElement({
            value: pendingFilters[filter.filterKey] || currentFilter[filter.filterKey] || undefined,
            onChange: (value: string) => handleFilterChange(filter.filterKey, value),
          })
        : filter.filterElement}
    </div>
  );

  return (
    <div>
      <div className="panel-title">
        <FormattedMessage id={"filter.panel.content.title"} />
      </div>
      <div className="panel-filters">
        {groupedFilters.default && <div className="filter-grid">{groupedFilters.default.map(renderFilterElement)}</div>}

        {Object.entries(groupedFilters).map(
          ([header, filters]) =>
            header !== "default" && (
              <div key={header}>
                <div>{filters.map(renderFilterElement)}</div>
              </div>
            ),
        )}
      </div>
      <Divider className="panel-divider" />
      <div className="panel-actions">
        <Button onClick={handleClearFilters}>
          <FormattedMessage id="filter.panel.button.clear" />
        </Button>
        <Button type="primary" onClick={handleApplyFilters}>
          <FormattedMessage id="filter.panel.button.apply" />
        </Button>
      </div>
    </div>
  );
};
